'use client'

import React from 'react'
import styles from './AGLocationsMap.module.scss'
import usePageLoad from '@/src/hooks/usePageLoad'
import { useRouter } from 'next/navigation'

interface MapMarker {
  id: string
  x: number
  y: number
  label: string
  link: string
  labelPosition?: 'above' | 'below'
}

function AGLocationsMapClient() {
  const router = useRouter()
  usePageLoad(() => {
    const markers: MapMarker[] = [
      {
        id: 'geelong',
        x: 440,
        y: 255,
        label: 'Geelong',
        link: 'geelong',
        labelPosition: 'below',
      },
      {
        id: 'melbourne',
        x: 530,
        y: 200,
        label: 'Melbourne',
        link: 'melbourne',
        labelPosition: 'above',
      },
      {
        id: 'mornington',
        x: 540,
        y: 290,
        label: 'Mornington<br />Peninsula',
        link: 'mornington',
        labelPosition: 'below',
      },
      {
        id: 'gippsland',
        x: 650,
        y: 265,
        label: 'Gippsland',
        link: 'gippsland',
        labelPosition: 'below',
      },
    ]

    const isMobile = () => {
      return (
        window.innerWidth <= 768 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      )
    }

    const mapConfig = {
      desktop: {
        initialZoom: 1.3,
        maxScale: 2,
        initialOffsetX: 50,
        initialOffsetY: 0,
        edgePadding: 0,
        enableInteraction: true,
      },
      mobile: {
        initialZoom: 0.9, // Fit all locations on mobile
        maxScale: 1.0, // No zooming on mobile
        initialOffsetX: 48,
        initialOffsetY: 0,
        edgePadding: 0,
        enableInteraction: false, // Static map on mobile
      },
    }

    // Get current config based on device
    const getCurrentConfig = () => {
      return isMobile() ? mapConfig.mobile : mapConfig.desktop
    }

    // Debounce function to prevent excessive updates during resize
    let resizeTimeout: NodeJS.Timeout
    const resizeHandler = () => {
      clearTimeout(resizeTimeout)

      // Set a timeout to update after resize stops
      resizeTimeout = setTimeout(() => {
        wrapperWidth = mapWrapper?.clientWidth ?? 0
        wrapperHeight = mapWrapper?.clientHeight ?? 0

        if (isMobile()) {
          minScale = Math.min(1.1, mapConfig.mobile.initialZoom)
          scale = minScale
          offsetX = mapConfig.mobile.initialOffsetX
          offsetY = mapConfig.mobile.initialOffsetY
        } else {
          const newScaleToFitHeight =
            mapHeight > 0 && wrapperHeight > 0 ? wrapperHeight / mapHeight : 1

          minScale = Math.max(newScaleToFitHeight, 0.1)

          if (scale < minScale) {
            scale = minScale
            offsetX = 0
            offsetY = 0
          }
        }

        updateMapTransform()

        requestAnimationFrame(() => {
          updateCoordinateDots()
          positionMarkersDirectly()

          requestAnimationFrame(() => {
            positionMarkersDirectly()
          })
        })
      }, 100)
    }

    const appElement = document.getElementsByClassName(styles.container)[0]
    if (appElement) {
      appElement.innerHTML = `
        <div class="${styles.mapContainer}">
          <div class="${styles.mapWrapper}">
            <div class="${styles.mapContent}">
              <img src="/images/VIC_Map.svg" alt="Interactive Map" class="${styles.mapImage}" draggable="false" />
              <div class="${styles.coordinateDotsContainer}"></div>
              <div class="${styles.markersContainer}"></div>
            </div>
          </div>
        </div>
        <div class="${styles.mapHelperText}">Use the mouse wheel to zoom in/out and left click and drag to pan around</div>
    `
    }

    const mapWrapper = document.getElementsByClassName(
      styles.mapWrapper,
    )[0] as HTMLDivElement | null

    const mapImage = document.getElementsByClassName(styles.mapImage)[0] as HTMLImageElement | null

    const markersContainer = document.getElementsByClassName(
      styles.markersContainer,
    )[0] as HTMLDivElement | null

    const currentConfig = getCurrentConfig()
    const isCurrentlyMobile = isMobile()

    let scale = 1
    let minScale = 1
    const maxScale = currentConfig.maxScale
    let offsetX = currentConfig.initialOffsetX
    let offsetY = currentConfig.initialOffsetY
    let isDragging = false
    let startX = 0
    let startY = 0
    let currentX = 0
    let currentY = 0
    let mapWidth = 0
    let mapHeight = 0
    let wrapperWidth = 0
    let wrapperHeight = 0
    let initialPinchDistance = 0
    let initialScale = 1
    let isPinching = false
    let hitBoundaryX = false
    let hitBoundaryY = false
    let wheelAnimationFrameId: number | null = null
    let animationFrameId: number | null = null

    function createMarkers(): void {
      if (!markersContainer) return

      markersContainer.innerHTML = ''
      const fragment = document.createDocumentFragment()

      markers.forEach((marker, index) => {
        const markerElement = document.createElement('div')
        markerElement.className = styles.marker
        markerElement.id = marker.id
        markerElement.dataset.x = marker.x.toString()
        markerElement.dataset.y = marker.y.toString()
        markerElement.dataset.index = index.toString()

        const x = marker.x
        const y = marker.y

        // Calculate marker scale based on current zoom level
        // Scale ranges from 1 (at minScale) to 0.5 (at maxScale)
        const zoomProgress = (scale - minScale) / (maxScale - minScale)
        const markerScale = 1 - zoomProgress * 0.5 // 1 at minScale, 0.5 at maxScale

        // Position exactly like dots but with transform for the arrow tip
        markerElement.style.position = 'absolute'
        markerElement.style.left = `${(x / mapWidth) * 100}%`
        markerElement.style.top = `${(y / mapHeight) * 100}%`
        // Use translate(-50%, -100%) to position the bottom tip at the exact coordinate
        markerElement.style.transform = `translate(-50%, -100%) scale(${markerScale})`
        markerElement.style.transformOrigin = 'center bottom'

        const labelPosition = marker.labelPosition || 'below'

        let markerContents = [
          `<div class="${styles.markerLabel}" style="color: black; position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%); margin-bottom: 5px; white-space: nowrap;">${marker.label}</div>`,
          `<img src="/images/marker.svg" alt="${marker.label}" class="${styles.markerIcon}" style="display: block;">`,
        ]
        if (labelPosition === 'below') {
          markerContents = markerContents.toReversed()
        }
        markerElement.innerHTML = markerContents.join('')

        const handleMarkerActivation = (e: Event): void => {
          e.stopPropagation()
          e.preventDefault()
          router.push(`/locations/${marker.link}`)
        }

        markerElement.addEventListener('click', handleMarkerActivation)
        markerElement.addEventListener('touchstart', e => e.stopPropagation(), { passive: true })
        markerElement.addEventListener(
          'touchend',
          e => {
            if (!isDragging && !isPinching) {
              handleMarkerActivation(e)
            }
          },
          { passive: false },
        )

        markerElement.addEventListener('mousedown', (e: MouseEvent) => {
          e.preventDefault()
        })
        markerElement.addEventListener('dragstart', (e: DragEvent) => {
          e.preventDefault()
        })

        fragment.appendChild(markerElement)
      })

      markersContainer.appendChild(fragment)
    }

    // Apply boundary constraints to keep map within view
    function applyBoundaryConstraints(): void {
      const scaledMapWidth = mapWidth * scale
      const scaledMapHeight = mapHeight * scale
      const mapOverflowsWidth = scaledMapWidth > wrapperWidth
      const mapOverflowsHeight = scaledMapHeight > wrapperHeight

      // If at minimum scale and no overflow, center the map
      if (scale <= minScale && !mapOverflowsHeight && !mapOverflowsWidth) {
        offsetX = 0
        offsetY = 0
        return
      }

      // Calculate padding with consistent 150px buffer in both directions
      // Scale the buffer based on the current zoom level
      const edgePadding = 150 * scale

      // Calculate max pan distances with the buffer
      const maxPanX = Math.max(0, (scaledMapWidth + edgePadding * 2 - wrapperWidth) / (2 * scale))
      const maxPanY = Math.max(0, (scaledMapHeight + edgePadding * 2 - wrapperHeight) / (2 * scale))

      // Apply constraints for both X and Y axes with the buffer
      // This allows dragging beyond the map edges by the buffer amount
      offsetX = Math.min(maxPanX, Math.max(-maxPanX, offsetX))
      offsetY = Math.min(maxPanY, Math.max(-maxPanY, offsetY))
    }

    // Update coordinate dots based on marker positions
    function updateCoordinateDots(): void {
      // Clear existing dots
      const dotsContainer = document.getElementsByClassName(styles.coordinateDotsContainer)[0]
      if (!dotsContainer) {
        console.error('Coordinate dots container not found')
        return
      }

      dotsContainer.innerHTML = ''

      // Create a dot for each marker
      markers.forEach(marker => {
        const x = marker.x
        const y = marker.y

        // Create a coordinate dot at the exact position (in percentage of map size)
        const dot = document.createElement('div')
        dot.className = styles.coordinateDot
        dot.style.position = 'absolute'
        dot.style.left = `${(x / mapWidth) * 100}%`
        dot.style.top = `${(y / mapHeight) * 100}%`
        dot.style.transform = 'translate(-50%, -50%)' // Center the dot on the exact coordinate
        dot.title = `Dot for ${marker.label} (${x},${y})`
        dotsContainer.appendChild(dot)
      })
    }

    // Position markers directly at the exact same coordinates as the dots
    function positionMarkersDirectly(): void {
      const markerElements = document.querySelectorAll<HTMLElement>('.marker')
      const markersContainer = document.getElementsByClassName(styles.markersContainer)[0]

      if (!markersContainer) {
        console.error('Markers container not found')
        return
      }

      // Calculate marker scale based on current zoom level
      // Scale ranges from 1 (at minScale) to 0.5 (at maxScale)
      const zoomProgress = (scale - minScale) / (maxScale - minScale)
      const markerScale = 1 - zoomProgress * 0.5 // 1 at minScale, 0.5 at maxScale

      // Position each marker at its exact coordinate
      markerElements.forEach((markerElement, index) => {
        const marker = markers[index]
        if (!marker) return

        const x = marker.x
        const y = marker.y

        // Position the marker exactly like the dots - using percentages
        // This ensures they're at the exact same position
        markerElement.style.position = 'absolute'
        markerElement.style.left = `${(x / mapWidth) * 100}%`
        markerElement.style.top = `${(y / mapHeight) * 100}%`

        // Adjust the transform to position the bottom tip of the marker at the exact coordinate
        // and apply the scale based on zoom level
        // Use translate(-50%, -100%) to position the bottom tip at the exact coordinate
        markerElement.style.transform = `translate(-50%, -100%) scale(${markerScale})`
        markerElement.style.transformOrigin = 'center bottom'
      })
    }

    // Update map transform based on current scale and offset
    function updateMapTransform(): void {
      const originalOffsetXBefore = offsetX
      const originalOffsetYBefore = offsetY
      applyBoundaryConstraints()
      hitBoundaryX = originalOffsetXBefore !== offsetX
      hitBoundaryY = originalOffsetYBefore !== offsetY

      // Apply transform to map content (which includes both dots and markers)
      // First center the map, then scale it, then apply the offset
      const transformValue = `translate(-50%, -50%) scale(${scale}) translate(${offsetX}px, ${offsetY}px)`

      // Apply transform to map content
      const mapContent = document.getElementsByClassName(styles.mapContent)[0] as HTMLDivElement
      if (mapContent) {
        mapContent.style.transform = transformValue
      } else {
        if (mapImage) mapImage.style.transform = transformValue
      }

      // Update coordinate dots
      updateCoordinateDots()

      // Position markers immediately
      positionMarkersDirectly()
    }

    // Handle zoom with center point
    function handleZoom(newScale: number, centerX: number, centerY: number): void {
      // Add safety checks for valid scale values
      if (!isFinite(newScale) || newScale <= 0) {
        // console.warn('Invalid scale value:', newScale, 'using current scale:', scale)
        return
      }

      // Ensure scale is within valid bounds
      const clampedScale = Math.max(minScale, Math.min(maxScale, newScale))

      if (clampedScale !== scale) {
        const rect = mapWrapper!.getBoundingClientRect()
        const zoomCenterX = centerX - rect.left
        const zoomCenterY = centerY - rect.top
        const wrapperCenterX = wrapperWidth / 2
        const wrapperCenterY = wrapperHeight / 2

        // Add safety checks for division by zero
        if (scale <= 0) {
          // console.warn('Current scale is invalid:', scale, 'resetting to minScale:', minScale)
          scale = minScale
          offsetX = 0
          offsetY = 0
          updateMapTransform()
          return
        }

        const mapPointX = (zoomCenterX - wrapperCenterX) / scale - offsetX
        const mapPointY = (zoomCenterY - wrapperCenterY) / scale - offsetY
        scale = clampedScale
        offsetX = (zoomCenterX - wrapperCenterX) / scale - mapPointX
        offsetY = (zoomCenterY - wrapperCenterY) / scale - mapPointY

        if (Math.abs(scale - minScale) < 0.01) {
          const scaledMapWidth = mapWidth * minScale
          const scaledMapHeight = mapHeight * minScale
          const mapOverflowsWidth = scaledMapWidth > wrapperWidth
          const mapOverflowsHeight = scaledMapHeight > wrapperHeight

          if (!mapOverflowsWidth && !mapOverflowsHeight) {
            offsetX = 0
            offsetY = 0
          }
          scale = minScale
        }

        updateMapTransform()
      }
    }

    // Add event listeners only if interactions are enabled
    if (mapWrapper && currentConfig.enableInteraction) {
      // Wheel event for zooming
      mapWrapper.addEventListener('wheel', (e: WheelEvent) => {
        e.preventDefault()
        if (wheelAnimationFrameId) {
          return
        }
        wheelAnimationFrameId = requestAnimationFrame(() => {
          const zoomFactor = scale < 1 ? 0.05 : 0.1
          const delta = e.deltaY > 0 ? -zoomFactor * scale : zoomFactor * scale
          const newScale = Math.max(minScale, Math.min(maxScale, scale + delta))
          handleZoom(newScale, e.clientX, e.clientY)
          wheelAnimationFrameId = null
        })
      })

      // Mouse down event for dragging
      mapWrapper.addEventListener('mousedown', (e: MouseEvent) => {
        if (e.target instanceof Element && e.target.closest('.marker')) {
          return
        }

        isDragging = true
        startX = e.clientX
        startY = e.clientY
        currentX = offsetX
        currentY = offsetY
        if (mapWrapper) {
          mapWrapper.style.cursor = 'grabbing'
        }
        hitBoundaryX = false
        hitBoundaryY = false
      })

      // Touch start event for mobile dragging and pinching
      mapWrapper.addEventListener(
        'touchstart',
        (e: TouchEvent) => {
          // Only prevent default if we're actually going to handle the touch
          if (e.target instanceof Element && e.target.closest('.marker')) {
            return
          }

          // Prevent default only when we start dragging/pinching the map
          e.preventDefault()

          if (e.touches.length === 1) {
            isDragging = true
            startX = e.touches[0].clientX
            startY = e.touches[0].clientY
            currentX = offsetX
            currentY = offsetY
            hitBoundaryX = false
            hitBoundaryY = false
          } else if (e.touches.length === 2) {
            const dx = e.touches[0].clientX - e.touches[1].clientX
            const dy = e.touches[0].clientY - e.touches[1].clientY
            initialPinchDistance = Math.sqrt(dx * dx + dy * dy)
            initialScale = scale
            isPinching = true
          }
        },
        { passive: false },
      )
    }

    // Window event listeners - only add if interactions are enabled
    if (currentConfig.enableInteraction) {
      window.addEventListener('mousemove', (e: MouseEvent) => {
        if (!isDragging) {
          return
        }

        e.preventDefault()
        const dx = (e.clientX - startX) / scale
        const dy = (e.clientY - startY) / scale

        if (!hitBoundaryX) {
          offsetX = currentX + dx
        }
        if (!hitBoundaryY) {
          offsetY = currentY + dy
        }

        if (animationFrameId) {
          return
        }

        animationFrameId = requestAnimationFrame(() => {
          updateMapTransform()
          animationFrameId = null
        })
      })

      window.addEventListener(
        'touchmove',
        (e: TouchEvent) => {
          // Only prevent default and handle touch if we're actually dragging/pinching the map
          if (!isDragging && !isPinching) {
            return // Allow normal page scrolling
          }

          e.preventDefault()

          if (isDragging && e.touches.length === 1) {
            const dx = (e.touches[0].clientX - startX) / scale
            const dy = (e.touches[0].clientY - startY) / scale

            if (!hitBoundaryX) {
              offsetX = currentX + dx
            }
            if (!hitBoundaryY) {
              offsetY = currentY + dy
            }

            if (animationFrameId) {
              return
            }

            animationFrameId = requestAnimationFrame(() => {
              updateMapTransform()
              animationFrameId = null
            })
          } else if (isPinching && e.touches.length === 2) {
            const dx = e.touches[0].clientX - e.touches[1].clientX
            const dy = e.touches[0].clientY - e.touches[1].clientY
            const currentDistance = Math.sqrt(dx * dx + dy * dy)
            const scaleFactor = currentDistance / initialPinchDistance
            const newScale = Math.max(minScale, Math.min(maxScale, initialScale * scaleFactor))
            const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2
            const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2
            handleZoom(newScale, centerX, centerY)
          }
        },
        { passive: false },
      )

      window.addEventListener('mouseup', () => {
        if (!isDragging) {
          return
        }

        isDragging = false
        if (mapWrapper) {
          mapWrapper.style.cursor = 'grab'
        }

        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId)
          animationFrameId = null
        }

        updateMapTransform()
      })

      window.addEventListener('touchend', () => {
        if (isDragging) {
          isDragging = false

          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId)
            animationFrameId = null
          }

          updateMapTransform()
        }

        if (isPinching) {
          isPinching = false
        }
      })
    }

    // Image load event handler
    if (mapImage) {
      mapImage.addEventListener('load', () => {
        mapWidth = mapImage.naturalWidth
        mapHeight = mapImage.naturalHeight
        wrapperWidth = mapWrapper ? mapWrapper.clientWidth : 0
        wrapperHeight = mapWrapper ? mapWrapper.clientHeight : 0
        if (wrapperWidth <= 0 || wrapperHeight <= 0) {
          setTimeout(
            () => {
              if (mapWrapper) {
                wrapperWidth = mapWrapper.clientWidth
                wrapperHeight = mapWrapper.clientHeight
                if (wrapperWidth > 0 && wrapperHeight > 0) {
                  // Recalculate and update using the same logic as main load
                  const retryScaleToFitHeight = mapHeight > 0 ? wrapperHeight / mapHeight : 1
                  const retryMinScale = Math.max(retryScaleToFitHeight, 0.1)

                  if (isCurrentlyMobile) {
                    minScale = Math.min(0.9, currentConfig.initialZoom)
                    scale = minScale
                    offsetX = currentConfig.initialOffsetX
                    offsetY = currentConfig.initialOffsetY
                  } else {
                    minScale = retryMinScale
                    scale = Math.max(minScale, Math.min(maxScale, currentConfig.initialZoom))
                  }

                  updateMapTransform()
                }
              }
            },
            isCurrentlyMobile ? 200 : 100,
          )
        }

        const scaleToFitHeight = mapHeight > 0 && wrapperHeight > 0 ? wrapperHeight / mapHeight : 1
        if (isCurrentlyMobile) {
          minScale = Math.min(1.1, currentConfig.initialZoom)
          scale = minScale
          offsetX = currentConfig.initialOffsetX
          offsetY = currentConfig.initialOffsetY
        } else {
          minScale = Math.max(scaleToFitHeight, 0.1)
          scale = Math.max(minScale, Math.min(maxScale, currentConfig.initialZoom))
        }

        if (mapWrapper && !isCurrentlyMobile) {
          const initialScaledMapWidth = mapWidth * scale
          const initialScaledMapHeight = mapHeight * scale
          const initialMapOverflowsWidth = initialScaledMapWidth > wrapperWidth
          const initialMapOverflowsHeight = initialScaledMapHeight > wrapperHeight

          if (initialMapOverflowsWidth || initialMapOverflowsHeight) {
            mapWrapper.style.cursor = 'grab'
          }
        }

        mapImage.style.transformOrigin = 'center center'
        updateMapTransform()
        createMarkers()
        positionMarkersDirectly()

        requestAnimationFrame(() => {
          updateMapTransform()
          positionMarkersDirectly()
        })

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            updateMapTransform()
            positionMarkersDirectly()
          })
        })

        if (mapWrapper) {
          mapWrapper.style.cursor = 'grab'
          mapWrapper.addEventListener('contextmenu', e => e.preventDefault())
          mapImage.addEventListener('dragstart', e => e.preventDefault())
        }

        window.addEventListener('resize', resizeHandler)
      })
    }
  })

  return <div className={styles.container} />
}

export default React.memo(AGLocationsMapClient)
